import type { GateContext, Lesson } from '../types';
import { f, run } from './helpers';
import { overshoot } from '../../simulation/metrics';
import { expandTrace } from '../../state/runRecord';

const Q_T = 1.5;
const pidSample = (ctx: GateContext) => ctx.sample('m7-pid', Q_T);

function stepOvershoot(ctx: GateContext, id: string) {
  return overshoot(expandTrace(run(ctx, id).trace), { tStart: 1, tEnd: 8, from: 2, to: 2.5, tolerance: 0.05 }).value ?? 0;
}

function thrustJaggedness(ctx: GateContext, id: string) {
  const T = run(ctx, id).trace.Tcmd;
  let tv = 0;
  for (let i = 1; i < T.length; i++) tv += Math.abs(T[i] - T[i - 1]);
  return tv / run(ctx, id).config.duration;
}

export const m7: Lesson = {
  id: 'm7',
  number: 7,
  title: 'PID control',
  summary: 'In this module, you will combine reaction, memory, and braking into a complete PID flight controller. Learn the step-by-step routine engineers use to tune each term without chaos, and see how filtering keeps sensor jitter from rattling your motors.',
  objectives: [
    'Attribute a correction to its P, I and D parts.',
    'Explain derivative-on-measurement at a target step.',
    'Choose an evidence-supported change for oscillation or noisy thrust.',
  ],
  controls: ['controller-kind', 'Kp', 'Ki', 'Kd', 'filter', 'noise', 'anti-windup', 'feedforward', 'contributions'],
  controllerKinds: ['pi', 'pid'],
  sections: [
    {
      id: 'm7-rule',
      title: 'Three terms',
      phase: 'learn',
      blocks: [
        {
          type: 'p',
          text: 'Proportional-Integral-Derivative (PID) control is the universal industry standard for autonomous flight, powering everything from consumer drones (PX4, ArduPilot, Betaflight) to rocket thrust-vector gimbals and aircraft flight controllers. It succeeds because it addresses the three temporal dimensions of physical motion simultaneously: present tracking error, past error accumulation, and future velocity trends.',
        },
        {
          type: 'p',
          text: 'The complete PID feedback correction combines all three terms into a single command:',
        },
        {
          type: 'math',
          tex: '\\term{ufb}{u_{fb}} = \\underbrace{\\term{Kp}{K_p}\\, \\term{error}{e}}_{\\term{uP}{u_P}} + \\underbrace{\\term{Ki}{K_i}\\, \\term{I}{I}}_{\\term{uI}{u_I}} - \\underbrace{\\term{Kd}{K_d}\\, \\term{vest}{v_{est}}}_{-\\term{uD}{u_D}}',
          aria: 'u f b equals K p times error e, which is u P, plus K i times I, which is u I, minus K d times the estimated velocity v est, which is u D.',
        },
        {
          type: 'table',
          caption: 'The three dimensions of PID control',
          headers: ['Term', 'Time horizon', 'Physical effect', 'Flight controller role'],
          rows: [
            ['{{uP|P}}', 'Present error ($e$)', 'Artificial spring stiffness', 'Pulls the aircraft toward the target altitude with force proportional to current displacement.'],
            ['{{uI|I}}', 'Past error ($\\int e\\,dt$)', 'Accumulated memory', 'Steadily ramps up holding force to eliminate persistent sag from gravity or unmodeled loads.'],
            ['{{uD|D}}', 'Future trend ($-v_{est}$)', 'Artificial viscous damping', 'Acts like a shock absorber by opposing vehicle speed, cushioning stops to prevent overshoot and bouncing.'],
          ],
        },
        {
          type: 'p',
          text: 'Professional flight test engineers follow a systematic tuning rule: **change only one term at a time**. On a new system, all gains start at zero. First, tune P for responsiveness (look for a fast initial rise on the graph). Next, add D to suppress overshoot and oscillations (look for flattened peaks and a smooth arrival at the target). Finally, add I to eliminate steady-state sag (look for the trace closing any persistent gap after a load is applied).',
        },
      ],
    },
    {
      id: 'm7-derivative',
      title: 'Which derivative?',
      phase: 'learn',
      blocks: [
        {
          type: 'p',
          text: 'Textbook control equations often write the derivative term as the **{{edot}}** ($K_d\\,\\dot e$). However, directly differentiating error in real flight software causes a severe malfunction known as **derivative kick**. Furthermore, differentiating real sensor signals amplifies electronic noise into violent motor jitter.',
        },
        {
          type: 'p',
          text: "Here is why derivative kick happens: when a flight controller receives a sudden step command (such as stepping the target from 2.0 m to 2.5 m), target altitude jumps instantaneously. Because error jumps in zero time, its rate of change $\\dot e$ is momentarily infinite! Multiplying an infinite rate by $K_d$ commands a massive, instantaneous thrust spike that can blow the motor's electronic speed controllers (ESCs) or saturate actuators.",
        },
        {
          type: 'p',
          text: 'To eliminate derivative kick, flight controllers use **{{derivative-on-measurement}}**: $-K_d\\,v_{est}$. Because the physical aircraft’s altitude cannot teleport, the measured velocity $v_{est}$ changes continuously, providing smooth damping without any command spikes.',
        },
        {
          type: 'p',
          text: 'Next consider sensor noise: real altimeters have slight electrical noise (e.g. $\\pm 1\\text{ cm}$). Over a $0.005\\text{ s}$ sampling step, a random $1\\text{ cm}$ jump looks like a false velocity of $2\\text{ m/s}$ ($0.01 / 0.005$)! Multiplied by $K_d = 8$, that injects $16\\text{ N}$ of high-frequency vibration into the motors. To prevent this jitter, raw velocity is passed through a first-order **{{filter|low-pass filter}}**: $\\alpha$ with time constant $\\tau$:',
        },
        {
          type: 'math',
          tex: '\\term{vest}{v_{est}} \\leftarrow \\term{vest}{v_{est}} + \\alpha\\,(v_{raw} - \\term{vest}{v_{est}}), \\qquad \\alpha = \\frac{\\term{dt}{\\Delta t}}{\\term{filter}{\\tau} + \\term{dt}{\\Delta t}}',
          aria: 'v est moves a fraction alpha of the way toward the raw rate each step, where alpha equals delta t divided by tau plus delta t.',
        },
        {
          type: 'p',
          text: 'The filter time constant $\\tau$ represents a core engineering trade-off: larger $\\tau$ filters out more sensor noise, but introduces phase lag that slows down the damping response.',
        },
      ],
    },
    {
      id: 'm7-predict',
      title: 'Predict',
      phase: 'predict',
      blocks: [
        {
          type: 'p',
          text: 'Consider how numerical differentiation amplifies high-frequency noise when calculating velocity rates.',
        },
        {
          type: 'predict',
          id: 'm7-p1',
          prompt: 'Sensor noise of about ±1 cm is added. What happens to the thrust request with an unfiltered D term?',
          options: [
            { id: 'nothing', text: 'Almost nothing. 1 cm is tiny.' },
            { id: 'jagged', text: 'It becomes jagged.' },
            { id: 'offset', text: 'It shifts to a constant offset.' },
          ],
          reveal: 'Because velocity is computed by dividing position change by a tiny time step ($\\Delta t = 0.005\\text{ s}$), a 1 cm measurement fluctuation appears as a massive 2 m/s velocity jump. Multiplied by $K_d$, this injects severe high-frequency chatter into the commanded thrust.',
        },
      ],
    },
    {
      id: 'm7-lab',
      title: 'PI vs PID, then noise',
      phase: 'experiment',
      blocks: [
        {
          type: 'p',
          text: 'In this lab, you will compare PI against full PID, observe the impact of sensor noise on derivative action, and tune a low-pass filter to restore smooth motor commands.',
        },
        {
          type: 'h',
          text: 'Part 1: Adding Derivative Action to a PI Controller',
        },
        {
          type: 'p',
          text: '**Purpose**: While integral action eliminates steady-state load sag, it adds phase lag that induces overshoot and oscillatory ringing. Adding derivative feedback ($u_D = -K_d\\,v_{est}$) injects artificial damping, suppressing overshoot and cushioning the arrival at target without sacrificing steady-state accuracy.',
        },
        {
          type: 'h',
          text: 'Part 2: Sensor Noise and Low-Pass Filtering',
        },
        {
          type: 'p',
          text: '**Purpose**: Differentiating real sensor signals amplifies high-frequency noise. Realistic altimeter noise ($\\pm 1\\text{ cm}$) causes the raw derivative term to generate violent motor jitter. Adding a first-order low-pass filter with time constant $\\tau$ smooths out commands, and you will tune $\\tau$ to find the optimal trade-off between noise rejection and damping responsiveness.',
        },
        { type: 'lab' },
      ],
    },
    {
      id: 'm7-inspect',
      title: 'Inspect',
      phase: 'inspect',
      blocks: [
        {
          type: 'p',
          text: 'Examine the telemetry logs to verify the damping and filtering trade-offs:',
        },
        {
          type: 'list',
          items: [
            'Compare the PI and PID runs: measure the reduction in overshoot at 1 s, and observe how the $u_D$ trace kicks in to brake the vehicle as it approaches target.',
            'Compare the unfiltered and filtered noisy runs: inspect the thrust plot to see how filtering eliminates jagged motor commands, and check whether excessive filtering (large $\\tau$) introduced unwanted sluggishness.',
          ],
        },
      ],
    },
  ],
  lab: {
    id: 'm7-lab',
    version: 1,
    instructions: 'Complete all four 15 s runs: Steps 1–2 compare PI against full PID; Steps 3–4 examine sensor noise amplification and low-pass filter tuning.',
    steps: [
      {
        id: 'm7-pi',
        title: 'Part 1: PI ($K_p = 12$, $K_i = 6$)',
        presetId: 'pid-step',
        instructions:
          'Load the step ($PI$ controller) and run to the end (15 s). Watch the altitude step at 1 s overshoot to ~2.7 m and ring before settling, and note the sag recovery when a 1 N load is applied at 8 s.',
        permitted: [],
        requires: 'A complete 15 s run.',
      },
      {
        id: 'm7-pid',
        title: 'Part 1: PID (add $K_d = 6$)',
        presetId: 'pid-step',
        setup: (c) => {
          c.controller.kind = 'pid';
        },
        instructions:
          'Load the step ($PID$ controller with $K_d = 6$) and run to the end (15 s). Watch the derivative term ($u_D$) actively brake upward velocity as the vehicle nears 2.5 m, cutting overshoot in half and cushioning the hover.',
        permitted: [],
        requires: 'A complete 15 s run.',
      },
      {
        id: 'm7-noise-raw',
        title: 'Part 2: PID with noise, unfiltered ($\\tau = 0$)',
        presetId: 'pid-step',
        setup: (c) => {
          c.controller.kind = 'pid';
          c.noise.enabled = true;
        },
        instructions:
          'Load the step (unfiltered noise) and run to the end (15 s). Look at the thrust plot to observe violent, high-frequency motor chatter caused by differentiating raw sensor noise (±1 cm).',
        permitted: [],
        requires: 'A complete 15 s run with noise on and τ = 0.',
      },
      {
        id: 'm7-noise-filt',
        title: 'Part 2: PID with noise, filtered ($0.02 \\le \\tau \\le 0.2$ s)',
        presetId: 'pid-step',
        setup: (c) => {
          c.controller.kind = 'pid';
          c.noise.enabled = true;
          c.controller.derivTau = 0.05;
        },
        instructions:
          'Load the filtered step (starts at τ = 0.05 s). Run to the end (15 s), then optionally adjust τ between 0.02 s and 0.2 s to see how filtering eliminates jagged motor spikes while preserving effective derivative damping.',
        permitted: ['controller.derivTau'],
        check: (r) => (r.config.controller.derivTau >= 0.02 && r.config.controller.derivTau <= 0.2 ? null : 'Use a filter τ between 0.02 and 0.2 s.'),
        requires: 'A complete 15 s run with noise on and 0.02 ≤ τ ≤ 0.2 s.',
      },
    ],
  },
  gate: {
    id: 'g7',
    version: 1,
    title: 'Check: PID',
    questions: [
      {
        id: 'g7-q1',
        version: 1,
        kind: 'numeric',
        binds: ['m7-pid'],
        prompt: (ctx) =>
          `Sample from your PID run at t = ${f(pidSample(ctx).t)} s ($K_p$ = 12, $K_i$ = 6, $K_d$ = 6). Compute the derivative contribution $u_D$.`,
        table: (ctx) => {
          const s = pidSample(ctx);
          return { caption: 'Sample', headers: ['e (m)', 'I (m·s)', 'v_est (m/s)'], rows: [[f(s.e, 4), f(s.I, 4), f(s.vEst, 4)]] };
        },
        unit: 'N',
        answer: (ctx) => -6 * Number(f(pidSample(ctx).vEst, 4)),
        tolerance: 0.01,
        misconceptions: [{ test: (x, ctx) => Math.abs(x - 6 * pidSample(ctx).vEst) < 0.01, feedback: 'The D term opposes motion. $u_D = -K_d v_{est}$.' }],
        hint: '$u_D = -K_d\\, v_{est}$.',
        explanation: 'Positive velocity gives negative $u_D$, braking the climb.',
        review: 'm7-rule',
      },
      {
        id: 'g7-q2',
        version: 1,
        kind: 'choice',
        binds: ['m7-pid'],
        prompt: () => `At that same moment, which term contributes the largest force (by size, ignoring sign)?`,
        table: (ctx) => {
          const s = pidSample(ctx);
          return { caption: 'Sample', headers: ['e (m)', 'I (m·s)', 'v_est (m/s)'], rows: [[f(s.e, 4), f(s.I, 4), f(s.vEst, 4)]] };
        },
        options: [
          { id: 'P', text: '$u_P = K_p e$' },
          { id: 'I', text: '$u_I = K_i I$' },
          { id: 'D', text: '$u_D = -K_d v_{est}$' },
        ],
        correct: (ctx) => {
          const s = pidSample(ctx);
          const mags = { P: Math.abs(12 * s.e), I: Math.abs(6 * s.I), D: Math.abs(6 * s.vEst) };
          return (Object.entries(mags).sort((a, b) => b[1] - a[1])[0][0]) as string;
        },
        hint: 'Compute all three: 12 e, 6 I, and 6 v_est.',
        explanation: 'Attributing corrections term by term is how you debug a PID.',
        review: 'm7-rule',
      },
      {
        id: 'g7-q3',
        version: 1,
        kind: 'choice',
        binds: [],
        prompt: () => 'At the target step (t = 1 s), why does $u_D$ not spike in our controller?',
        options: [
          { id: 'meas', text: 'D uses the rate of the measured altitude, which can’t jump. The error jumps but D doesn’t use it.' },
          { id: 'filter', text: 'The filter removes the spike.', feedback: 'Your noise-free PID run has τ = 0 and still no kick.' },
          { id: 'small', text: '$K_d$ is too small to matter.', feedback: 'With derivative on error, even a small $K_d$ × a huge momentary $\\dot e$ spikes.' },
          { id: 'ff', text: 'Feedforward cancels it.', feedback: 'Feedforward is constant $T_0$ here.' },
        ],
        correct: () => 'meas',
        hint: 'What does the D term take as input?',
        explanation: 'Derivative on measurement avoids derivative kick at reference steps.',
        review: 'm7-derivative',
      },
      {
        id: 'g7-q4',
        version: 1,
        kind: 'choice',
        binds: ['m7-pi', 'm7-pid'],
        prompt: (ctx) =>
          `Your PI run overshot the step by ${f(stepOvershoot(ctx, 'm7-pi') * 100, 0)} %, your PID run by ${f(stepOvershoot(ctx, 'm7-pid') * 100, 0)} %. If a PI controller oscillates too much, what do those numbers suggest trying?`,
        options: [
          { id: 'kd', text: 'Add derivative action ($K_d$ > 0) for damping.' },
          { id: 'ki', text: 'Increase $K_i$.', feedback: 'More integral action tends to add oscillation, not remove it.' },
          { id: 'kp', text: 'Increase $K_p$.', feedback: 'Stiffer without extra damping rings more (Module 4).' },
          { id: 'ff', text: 'Turn off feedforward.', feedback: 'That removes the hover prediction. The loop then has to do all the work.' },
        ],
        correct: () => 'kd',
        hint: 'Compare the two overshoot numbers.',
        explanation: 'D adds damping, which reduced overshoot in your runs.',
        review: 'm7-inspect',
      },
      {
        id: 'g7-q5',
        version: 1,
        kind: 'choice',
        binds: ['m7-noise-raw', 'm7-noise-filt'],
        prompt: (ctx) =>
          `Average thrust change per second: unfiltered ${f(thrustJaggedness(ctx, 'm7-noise-raw'), 0)} N/s, filtered (τ = ${run(ctx, 'm7-noise-filt').config.controller.derivTau} s) ${f(thrustJaggedness(ctx, 'm7-noise-filt'), 0)} N/s. The noisy thrust is jagged. Looking at those numbers, what should you try?`,
        options: [
          { id: 'filter', text: 'Filter the velocity estimate (τ of a few hundredths of a second).' },
          { id: 'morekd', text: 'Increase $K_d$.', feedback: 'That multiplies the noisy velocity estimate by more, making it worse.' },
          { id: 'ki', text: 'Increase $K_i$.', feedback: 'The jitter comes from the D path, which $K_i$ does not affect.' },
          { id: 'none', text: 'Nothing. Jagged thrust is harmless.', feedback: 'Your unfiltered run also shows saturation from the jitter. Real motors heat up and wear.' },
        ],
        correct: () => 'filter',
        hint: 'Which run had much less thrust jitter?',
        explanation: 'Smoothing $v_{est}$ trades a slightly slower D response for far less noise-driven thrust.',
        review: 'm7-derivative',
      },
    ],
  },
};
